<template>
    <div>
        <v-data-table :loading="loading" :loader-height="5" :items="filteredCompanies" :headers="companyTableHeader"
                      show-select v-model="selectedCompanies" sort-by="iid">
            <template v-slot:body.prepend>
                <tr>
                    <td/>
                    <td v-for="header in companyTableHeader" :key="`filter-${header.value}`" class="pa-1">
                        <v-select
                            v-if="header.value === 'status'"
                            v-model="companyColumnFilters[header.value]"
                            :items="statusFilterOptions"
                            dense
                            outlined
                            hide-details
                            clearable
                            placeholder="Status auswählen"
                        />
                        <v-select
                            v-else-if="header.value === 'type'"
                            v-model="companyColumnFilters[header.value]"
                            :items="companyTypeFilterOptions"
                            dense
                            outlined
                            hide-details
                            clearable
                            placeholder="Art auswählen"
                        />
                        <v-select
                            v-else-if="header.value === 'nextPaymentLicenses'"
                            v-model="companyColumnFilters[header.value]"
                            :items="nextPaymentFilterOptions"
                            dense
                            outlined
                            hide-details
                            clearable
                            placeholder="Nächste Zahlung filtern"
                        />
                        <v-select
                            v-else-if="header.value === 'nextInspection'"
                            v-model="companyColumnFilters[header.value]"
                            :items="nextInspectionFilterOptions"
                            dense
                            outlined
                            hide-details
                            clearable
                            placeholder="Kontrolle filtern"
                        />
                        <div v-else-if="header.value === 'strikes'">
                            <div class="d-flex align-center flex-nowrap">
                                <v-select
                                    v-model="strikesFilterOperator"
                                    :items="strikesFilterOperators"
                                    dense
                                    outlined
                                    hide-details
                                    single-line
                                    class="mr-1 flex-grow-0"
                                    style="width: 72px; min-width: 72px;"
                                />
                                <v-text-field
                                    v-model="companyColumnFilters[header.value]"
                                    type="number"
                                    dense
                                    outlined
                                    hide-details
                                    clearable
                                    placeholder="Strikes"
                                    class="flex-grow-1"
                                />
                            </div>
                        </div>
                        <v-text-field
                            v-else-if="isFilterableColumn(header.value)"
                            v-model="companyColumnFilters[header.value]"
                            dense
                            outlined
                            hide-details
                            :placeholder="`Suche ${header.text}`"
                        />
                    </td>
                </tr>
            </template>

            <template v-slot:item.licensePrice="{ item }">
                {{ formatMoney(getTotalCommercialFees(item) * 4) }}
            </template>
            <template v-slot:item.strikes="{ item }">
                <v-chip small color="error" dark v-if="getStrikeCount(item.strikes) >= strikeHighlightThreshold">
                    {{ getStrikeCount(item.strikes) }}
                </v-chip>
                <span v-else>{{ getStrikeCount(item.strikes) }}</span>
            </template>
            <template v-slot:item.type="{ item }">
                {{ companyTypes[item.type] || 'Unbekannt' }}
            </template>
            <template v-slot:item.owner.name="{ item }">
                <router-link class="text--secondary" v-if="item.owner" :to="'/criminal-record/' + item.owner.id">
                    {{ item.owner.name }}
                </router-link>
                <span v-else>Gewerbeamt</span>
            </template>

            <template v-slot:item.nextPaymentLicenses="{ item }">
                {{ formatDateToInXDays(item.nextPaymentLicenses) }}
            </template>

            <template v-slot:item.nextInspection="{ item }">
                <span v-if="getNextInspectionDate(item)" :class="isInspectionOverdue(item) ? 'error--text font-weight-bold' : ''">
                    <v-icon small v-if="isInspectionOverdue(item)" color="error" class="mr-1">mdi-alert-circle-outline</v-icon>
                    {{ formatDateToInXDays(getNextInspectionDate(item)) }}
                </span>
                <span v-else class="grey--text">Keine Kontrolle</span>
            </template>

            <template v-slot:item.status="{ item }">
                <v-btn icon color="error" v-if="getStrikeCount(item.strikes) >= strikeHighlightThreshold">
                    <v-icon size="24">mdi-store-alert-outline</v-icon>
                </v-btn>
                <v-btn icon color="grey" v-else-if="!item.owner">
                    <v-icon size="24">mdi-account-alert-outline</v-icon>
                </v-btn>
                <v-btn icon color="warning" v-else-if="!item.owner.phoneNumber">
                    <v-icon size="24">mdi-phone-alert-outline</v-icon>
                </v-btn>
                <v-btn icon color="info" v-else-if="isOnVacation(item.vacatedUntil)">
                    <v-icon size="24">mdi-tsunami</v-icon>
                </v-btn>
                <v-btn icon color="error" v-else-if="isPaymentOverdue(item.nextPaymentLicenses)">
                    <v-icon size="24">mdi-currency-eur-off</v-icon>
                </v-btn>
                <v-btn icon color="warning" v-else-if="isPaymentNearlyOverdue(item.nextPaymentLicenses)">
                    <v-icon>mdi-credit-card-clock-outline</v-icon>
                </v-btn>


                <v-btn icon color="success" v-else>
                    <v-icon size="24">mdi-check-all</v-icon>
                </v-btn>
            </template>

            <template v-slot:item.actions="{ item }">
                <v-btn icon :to="'/company/' + item.id">
                    <v-icon color="primary">mdi-eye-outline</v-icon>
                </v-btn>
                <v-btn icon @click="quickPay(item)">
                    <v-icon color="orange">mdi-currency-eur</v-icon>
                </v-btn>
                <v-btn icon @click="openQuickInspection(item)">
                    <v-icon color="teal">mdi-clipboard-check-outline</v-icon>
                </v-btn>
                <v-btn icon @click="deleteItem(item)" v-if="user.admin || user.orgAdmin">
                    <v-icon color="error">mdi-delete-variant</v-icon>
                </v-btn>
            </template>
        </v-data-table>

        <v-dialog v-model="quickPayModal" persistent width="300">
            <v-card dark :loading="quickPaySaving">
                <v-card-title class="pa-1 pl-4">
                    <v-icon left color="yellow">mdi-currency-eur</v-icon>
                    Zahlung erfassen
                </v-card-title>
                <v-divider/>
                <v-card-text>
                    <v-slider v-model="quickPayWeeks" max="4" min="1" :label="String(quickPayWeeks)" hint="Interval in Wochen"
                              persistent-hint/>
                    <br/>
                    Preis: <b>{{ formatMoney(quickPayPrice) }}</b>
                    <div class="caption mt-2" v-if="quickPaySummary.billedWeeks > 0">
                        Berechnet für {{ quickPaySummary.billedWeeks }} Woche:n
                        <span v-if="quickPaySummary.retroactiveWeeks > 0">
                            (davon {{ quickPaySummary.retroactiveWeeks }} Woche:n rückwirkend)
                        </span>
                    </div>
                    <div class="caption" v-if="quickPaySummary.arrearsCarryoverWeeks > 0">
                        {{ quickPaySummary.arrearsCarryoverWeeks }} offene Woche:n werden in den neuen Intervall übernommen. Nächste Zahlung in {{ quickPaySummary.weeksUntilNextPayment }} Woche:n.
                    </div>
                </v-card-text>
                <v-card-actions>
                    <v-btn x-small color="blue" @click="generateLicenseText(quickPayCompany)" v-if="user.admin || userIsOfAnyAccountType(['GOVERNMENT'])" :disabled="!quickPayCompany">
                        Lizenztext
                    </v-btn>
                </v-card-actions>
                <v-divider/>
                <v-card-actions>
                    <v-btn x-small color="success" @click="quickPayComplete()">
                        Zahlung erhalten
                    </v-btn>
                    <v-spacer/>
                    <v-btn x-small color="grey" @click="quickPayModal=false">
                        Abbrechen
                    </v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>
        <v-dialog v-model="quickInspectionModal" persistent width="500">
            <v-card dark :loading="quickInspectionSaving">
                <v-card-title class="pa-1 pl-4">
                    <v-icon left color="teal">mdi-clipboard-check-outline</v-icon>
                    Kontrolle erfassen
                </v-card-title>
                <v-divider/>
                <v-card-text class="pt-4">
                    <v-textarea v-model="quickInspectionNote" outlined dense label="Notiz" rows="3" auto-grow/>
                    <v-checkbox v-model="quickInspectionFollowUp" label="Nachkontrolle erforderlich" dense hide-details class="mb-2"/>
                    <v-text-field
                        v-if="quickInspectionFollowUp"
                        v-model.number="quickInspectionFollowUpDays"
                        type="number"
                        outlined
                        dense
                        label="Nachkontrolle in (Tagen)"
                        min="1"
                        class="mb-2"
                    />
                    <v-divider class="mb-3"/>
                    <v-text-field
                        v-model="quickInspectionEvidenceInput"
                        dense
                        outlined
                        clearable
                        clear-icon="mdi-delete"
                        prepend-inner-icon="mdi-archive"
                        hint="Beweismaterial aus dem Zwischenspeicher einfügen oder Bild-URL eingeben"
                        persistent-hint
                        placeholder="Beweismaterial einfügen oder URL eingeben"
                        @paste.prevent="onQuickInspectionEvidencePaste"
                        @change="onQuickInspectionEvidenceUrlInput"
                        @click:clear="quickInspectionEvidenceInput = null"
                    />
                    <v-row v-if="quickInspectionEvidences.length > 0" class="mt-1 mx-0">
                        <v-col cols="4" v-for="(ev, k) in quickInspectionEvidences" :key="k" class="pa-1">
                            <v-img
                                v-if="ev.type === 'BASE64_IMAGE'"
                                @click="quickInspectionEvidences.splice(k, 1)"
                                :height="256/16*9" :width="256"
                                :src="'data:image/png;base64,' + ev.image"
                                style="cursor:pointer"
                            />
                            <v-img
                                v-else-if="ev.type === 'URL'"
                                @click="quickInspectionEvidences.splice(k, 1)"
                                :height="256/16*9" :width="256"
                                :src="ev.image"
                                style="cursor:pointer"
                            />
                        </v-col>
                    </v-row>
                </v-card-text>
                <v-divider/>
                <v-card-actions>
                    <v-btn x-small color="success" :loading="quickInspectionSaving" @click="quickInspectionComplete()">
                        Kontrolle speichern
                    </v-btn>
                    <v-spacer/>
                    <v-btn x-small color="grey" @click="quickInspectionModal=false">
                        Abbrechen
                    </v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>
    </div>
</template>
<script>
import moment          from "moment";
import {COMPANY_TYPES} from "@/plugins/config";
import {COMPANY_STRIKE_HIGHLIGHT_THRESHOLD} from "@/config";
import Swal            from "sweetalert2";

export default {
    data:     () => ({
        loading:   true,
        companies: [],

        quickPayModal:   false,
        quickPayCompany: null,
        quickPayWeeks:   4,
        quickPaySaving:  false,

        quickInspectionModal:      false,
        quickInspectionCompany:    null,
        quickInspectionSaving:     false,
        quickInspectionNote:       '',
        quickInspectionFollowUp:   false,
        quickInspectionFollowUpDays: 7,
    quickInspectionEvidences:     [],
    quickInspectionEvidenceInput: null,

        companyTableHeader: [
            {value: 'iid', text: 'ID', width: '50px'},
            {value: 'status', text: 'Status', align: 'center', width: '70px'},
            {value: 'name', text: 'Name'},
            {value: 'type', text: 'Art'},
            {value: 'location', text: 'Adresse', width: '280px'},
            {value: 'owner.name', text: 'Besitzer', width: '210px'},
            {value: 'owner.phoneNumber', text: 'Tel. Nr.'},
            {value: 'strikes', text: 'Strikes'},
            {value: 'licensePrice', text: 'Lizenzkosten'},
            {value: 'nextPaymentLicenses', text: 'Nächste Zahlung in'},
            {value: 'nextInspection', text: 'Nächste Kontrolle'},

            {value: 'actions', text: 'Aktionen', width: '190px'},
        ],

        selectedCompanies: [],

        companyColumnFilters: {
            iid:                 '',
            status:              '',
            name:                '',
            type:                '',
            location:            '',
            'owner.name':        '',
            'owner.phoneNumber': '',
            strikes:             '',
            licensePrice:        '',
            nextPaymentLicenses: '',
            nextInspection:      '',
            actions:             '',
        },
        strikesFilterOperator: '=',
        strikesFilterOperators: ['>', '<', '='],
        strikeHighlightThreshold: COMPANY_STRIKE_HIGHLIGHT_THRESHOLD,
    }),
    computed: {
        companyTypes() {
            return COMPANY_TYPES;
        },
        statusFilterOptions() {
            return [
                'Strikes',
                'Kein Besitzer',
                'Keine Telefonnummer',
                'Beurlaubt',
                'Im Zahlungsverzug',
                'Zahlung bevorstehend',
                'OK',
            ];
        },
        companyTypeFilterOptions() {
            return Object.values(this.companyTypes).filter(Boolean);
        },
        nextPaymentFilterOptions() {
            return [
                {text: 'In der Vergangenheit', value: 'PAST'},
                {text: 'In weniger als 1 Woche', value: 'LESS_THAN_WEEK'},
                {text: 'Kein Termin', value: 'NO_DATE'},
            ];
        },
        nextInspectionFilterOptions() {
            return [
                {text: 'Überfällig', value: 'OVERDUE'},
                {text: 'Fällig in < 3 Tagen', value: 'SOON'},
                {text: 'Keine Kontrolle eingetragen', value: 'NONE'},
            ];
        },
        filteredCompanies() {
            return this.companies.filter((company) => {
                return Object.entries(this.companyColumnFilters).every(([column, searchTerm]) => {
                    if (!this.isFilterableColumn(column)) {
                        return true;
                    }

                    if (column === 'strikes') {
                        return this.matchesStrikesFilter(searchTerm, company);
                    }

                    if (column === 'nextPaymentLicenses') {
                        return this.matchesNextPaymentFilter(searchTerm, company);
                    }

                    if (column === 'nextInspection') {
                        return this.matchesNextInspectionFilter(searchTerm, company);
                    }

                    const normalizedSearch = this.normalizeFilterValue(searchTerm);
                    if (!normalizedSearch) {
                        return true;
                    }

                    const columnValue = this.getCompanyColumnValue(company, column);

                    if (this.isSelectFilterColumn(column)) {
                        return this.normalizeFilterValue(columnValue) === normalizedSearch;
                    }

                    return this.normalizeFilterValue(columnValue).includes(normalizedSearch);
                });
            });
        },
        quickPaySummary() {
            return this.buildQuickPaySummary(this.quickPayCompany, this.quickPayWeeks);
        },
        quickPayPrice() {
            return this.quickPaySummary.totalPrice;
        }
    },
    methods:  {
        normalizeFilterValue(value) {
            if (value === null || value === undefined) {
                return '';
            }

            return String(value).toLowerCase().trim();
        },
        isFilterableColumn(column) {
            return column !== 'actions';
        },
        isSelectFilterColumn(column) {
            return column === 'status' || column === 'type';
        },
        matchesStrikesFilter(searchTerm, company) {
            if (searchTerm === '' || searchTerm === null || searchTerm === undefined) {
                return true;
            }

            const filterValue = Number(searchTerm);
            if (Number.isNaN(filterValue)) {
                return true;
            }

            const strikeCount = this.getStrikeCount(company.strikes);
            switch (this.strikesFilterOperator) {
                case '>':
                    return strikeCount > filterValue;
                case '<':
                    return strikeCount < filterValue;
                case '=':
                default:
                    return strikeCount === filterValue;
            }
        },
        matchesNextPaymentFilter(searchTerm, company) {
            if (!searchTerm) {
                return true;
            }

            const nextPaymentDate = company.nextPaymentLicenses;
            const now             = moment();

            if (searchTerm === 'NO_DATE') {
                return !nextPaymentDate;
            }

            if (!nextPaymentDate) {
                return false;
            }

            const paymentMoment = moment(nextPaymentDate);
            if (!paymentMoment.isValid()) {
                return false;
            }

            switch (searchTerm) {
                case 'PAST':
                    return paymentMoment.isBefore(now);
                case 'LESS_THAN_WEEK':
                    return paymentMoment.isSameOrAfter(now) && paymentMoment.isBefore(moment(now).add(7, 'days'));
                default:
                    return true;
            }
        },
        getCompanyColumnValue(company, column) {
            switch (column) {
                case 'status':
                    return this.getCompanyStatusLabel(company);
                case 'type':
                    return this.companyTypes[company.type] || 'Unbekannt';
                case 'sharedSigns':
                    return this.getSharedSignLabels(company).join(', ');
                case 'owner.name':
                    return company.owner ? company.owner.name : 'Gewerbeamt';
                case 'strikes':
                    return this.getStrikeCount(company.strikes);
                case 'licensePrice':
                    return this.getTotalCommercialFees(company) * 4;
                case 'nextPaymentLicenses':
                    return this.formatDateToInXDays(company.nextPaymentLicenses);
                case 'nextInspection':
                    return this.getNextInspectionDate(company) ? this.formatDateToInXDays(this.getNextInspectionDate(company)) : '';
                default:
                    return this.getNestedValue(company, column);
            }
        },
        getCompanyStatusLabel(company) {
            if (this.getStrikeCount(company.strikes) >= this.strikeHighlightThreshold) {
                return 'Strikes';
            }

            if (!company.owner) {
                return 'Kein Besitzer';
            }

            if (!company.owner.phoneNumber) {
                return 'Keine Telefonnummer';
            }

            if (this.isOnVacation(company.vacatedUntil)) {
                return 'Beurlaubt';
            }

            if (this.isPaymentOverdue(company.nextPaymentLicenses)) {
                return 'Im Zahlungsverzug';
            }

            if (this.isPaymentNearlyOverdue(company.nextPaymentLicenses)) {
                return 'Zahlung bevorstehend';
            }

            return 'OK';
        },
        getNestedValue(sourceObject, path) {
            return path.split('.').reduce((currentValue, pathPart) => {
                if (currentValue === null || currentValue === undefined) {
                    return '';
                }
                return currentValue[pathPart];
            }, sourceObject);
        },
        deleteItem(company) {
            Swal.fire({
                title:             'Bist du dir sicher?',
                html:              `Diese Aktion kann nicht rückgängig gemacht werden. Möchtest du das Gewerbe <b>${company.label}</b> wirklich löschen?`,
                showCancelButton:  true,
                cancelButtonText:  "Abbrechen",
                confirmButtonText: "Löschen"
            }).then(r => {
                if (r.isConfirmed) {
                    this.loading = true;
                    this.apiDeleteCompany(company).then(() => {
                        this.fetchCompanies();
                    })
                }
            })
        },
        openQuickInspection(company) {
            this.quickInspectionCompany       = company;
            this.quickInspectionNote          = '';
            this.quickInspectionFollowUp      = false;
            this.quickInspectionFollowUpDays  = 7;
            this.quickInspectionEvidences     = [];
            this.quickInspectionEvidenceInput = null;
            this.quickInspectionModal         = true;
        },
        onQuickInspectionEvidencePaste(event) {
            const item = event.clipboardData?.items?.[0];
            if (!item) return;
            if (item.kind === 'file') {
                const file = item.getAsFile();
                this.toBase64(file).then(r => {
                    this.quickInspectionEvidences.push({title: 'Beweisfoto', type: 'BASE64_IMAGE', image: r});
                });
            } else if (item.kind === 'string') {
                item.getAsString(s => {
                    const trimmed = (s || '').trim();
                    if (trimmed.length > 0) {
                        this.quickInspectionEvidences.push({title: 'Beweisfoto', type: 'URL', image: trimmed});
                    }
                });
            }
            this.quickInspectionEvidenceInput = null;
        },
        onQuickInspectionEvidenceUrlInput(val) {
            const trimmed = (val || '').trim();
            if (trimmed.length > 0) {
                this.quickInspectionEvidences.push({title: 'Beweisfoto', type: 'URL', image: trimmed});
                this.quickInspectionEvidenceInput = null;
            }
        },
        async quickInspectionComplete() {
            this.quickInspectionSaving = true;
            try {
                const attachmentIds = [];
                const urlEvidences  = [];

                for (const ev of this.quickInspectionEvidences) {
                    if (ev.type === 'BASE64_IMAGE') {
                        const response = await this.apiUploadEvidence(ev.image, 'inspection-evidence.png', 'Gewerbekontrolle', true);
                        if (response?.data?.id) {
                            attachmentIds.push(response.data.id);
                        }
                    } else if (ev.type === 'URL') {
                        urlEvidences.push(ev.image);
                    }
                }

                let note = (this.quickInspectionNote || '').trim();
                if (urlEvidences.length > 0) {
                    const urlNote = urlEvidences.map(url => `- ${url}`).join('\n');
                    note = note ? `${note}\n\nBeweis-Links:\n${urlNote}` : `Beweis-Links:\n${urlNote}`;
                }

                await this.apiCompanyCreateInspection(this.quickInspectionCompany, {
                    note,
                    followUp:              this.quickInspectionFollowUp,
                    followUpDays:          this.quickInspectionFollowUpDays,
                    evidenceAttachmentIds: attachmentIds,
                });
                await this.fetchCompanies();
                this.quickInspectionModal   = false;
                this.quickInspectionCompany = null;
            } catch (err) {
                Swal.fire({
                    icon: 'error',
                    title: 'Kontrolle konnte nicht gespeichert werden',
                    text: err?.response?.data?.message || err?.message || 'Unbekannter Fehler',
                });
            } finally {
                this.quickInspectionSaving = false;
            }
        },
        quickPay(company) {
            this.quickPayModal   = true;
            this.quickPayCompany = company;
        },
        quickPayComplete() {
            this.quickPaySaving = true;

            const paymentSummary = this.buildQuickPaySummary(this.quickPayCompany, this.quickPayWeeks);
            const nextCycle = paymentSummary.nextCycleIso;

            this.apiUpdateCompany(this.quickPayCompany.id, {
                nextPaymentLicenses: nextCycle
            }).then(() => {
                Promise.all([
                    this.fetchCompanies(),
                    this.apiCreateCompanyRecordEntry(this.quickPayCompany, {
                        type:    'PAYMENT',
                        content: this.buildQuickPayRecordContent(paymentSummary),
                        fine:    paymentSummary.totalPrice
                    })
                ]).then(() => {
                    this.quickPaySaving  = false;
                    this.quickPayModal   = false;
                    this.quickPayCompany = null;
                })
            })
        },
        getNextInspectionDate(company) {
            const inspections = Array.isArray(company?.inspections) ? company.inspections : [];
            if (inspections.length === 0) return null;
            // find the most recently created inspection and return its nextInspectionAt
            const latest = inspections.reduce((prev, cur) => {
                return moment(cur.createdAt).isAfter(moment(prev.createdAt)) ? cur : prev;
            });
            return latest.nextInspectionAt || null;
        },
        isInspectionOverdue(company) {
            const date = this.getNextInspectionDate(company);
            if (!date) return false;
            return moment(date).isBefore(moment());
        },
        matchesNextInspectionFilter(searchTerm, company) {
            if (!searchTerm) return true;
            const date = this.getNextInspectionDate(company);
            const now = moment();
            switch (searchTerm) {
                case 'NONE':
                    return !date;
                case 'OVERDUE':
                    return !!date && moment(date).isBefore(now);
                case 'SOON':
                    return !!date && moment(date).isSameOrAfter(now) && moment(date).isBefore(moment(now).add(3, 'days'));
                default:
                    return true;
            }
        },
        isPaymentNearlyOverdue(date) {
            if (date) return moment(date).subtract(7, 'd').isBefore(moment());
            return false;
        },
        isPaymentOverdue(date) {
            if (date) return moment(date).isBefore(moment());
            return false;
        },
        isOnVacation(date) {
            if (date) return moment(date).isAfter(moment());
            return false;
        },

        formatDateToInXDays(val) {
            if (!val) return '-';
            const now = moment().startOf('day');
            const end = moment(val).startOf('day');
            const diffDays = end.diff(now, 'days');
            const absDays = Math.abs(diffDays);
            const past = diffDays < 0;
            if (absDays === 0) return 'heute';
            const weeks = Math.floor(absDays / 7);
            if (weeks >= 1) {
                const label = weeks === 1 ? '1 Woche' : `${weeks} Wochen`;
                return past ? `vor ${label}` : `in ${label}`;
            }
            const label = absDays === 1 ? '1 Tag' : `${absDays} Tagen`;
            return past ? `vor ${label}` : `in ${label}`;
        },

        getStrikeCount(strikes) {
            let count = 0;
            let now   = moment();

            for (let strike of strikes) {
                if (moment(strike.expiresAfter).isAfter(now) && !strike.revoked) {
                    count += strike.points;
                }
            }

            return count;
        },
        formatSignLabel(sign) {
            if (!sign) return '';

            const parts = [`Schild #${sign.iid ?? '?'}`];
            if (sign.postalCode) {
                parts.push(`PLZ ${sign.postalCode}`);
            }

            return parts.join(' - ');
        },
        getSharedSignLabels(company) {
            const source = Array.isArray(company?.signLinks) ? company.signLinks : [];
            const labels = new Set();

            for (const link of source) {
                const sign = link?.sign;
                const linkedSigns = Array.isArray(sign?.linkedSigns) ? sign.linkedSigns : [];
                if (linkedSigns.length === 0) continue;

                labels.add(this.formatSignLabel(sign));
            }

            return Array.from(labels);
        },
        getLicenseFees(links) {
            const source = Array.isArray(links) ? links : [];
            let price = 0;

            for (let link of source) {
                price += link.count * link.license.fee;
            }

            return price;
        },
        getSignFees(signLinks) {
            const source = Array.isArray(signLinks) ? signLinks : [];
            let price = 0;

            for (let link of source) {
                price += Number(link?.sign?.fee) || 0;
            }

            return price;
        },
        getTotalCommercialFees(company) {
            const licenseFees = this.getLicenseFees(Array.isArray(company?.licenseLinks) ? company.licenseLinks : []);
            const signFees = this.getSignFees(company?.signLinks);
            return licenseFees + signFees;
        },

        fetchCompanies() {
            this.loading = true;
            return this.apiGetAllCompanies().then(r => {
                this.companies = r.data;
                this.loading   = false;
                return true;
            }).catch(err => {
                Swal.fire({
                    icon: 'error',
                    title: 'Gewerbe konnten nicht geladen werden',
                    text: err?.response?.data?.message || err?.message || 'Unbekannter Fehler',
                });
                return false;
            }).finally(() => {
                this.loading = false;
            })
        },

        escapeHtml(value) {
            return String(value ?? '')
                .replace(/&/g, '&amp;')
                .replace(/</g, '&lt;')
                .replace(/>/g, '&gt;')
                .replace(/"/g, '&quot;')
                .replace(/'/g, '&#039;');
        },

        copyTextToClipboard(text) {
            const normalizedText = String(text ?? '');

            if (navigator?.clipboard?.writeText) {
                return navigator.clipboard.writeText(normalizedText);
            }

            return new Promise((resolve, reject) => {
                try {
                    const textArea = document.createElement('textarea');
                    textArea.value = normalizedText;
                    textArea.setAttribute('readonly', '');
                    textArea.style.position = 'fixed';
                    textArea.style.left = '-9999px';
                    document.body.appendChild(textArea);
                    textArea.select();

                    const copied = document.execCommand('copy');
                    document.body.removeChild(textArea);

                    if (copied) {
                        resolve();
                    } else {
                        reject(new Error('Kopieren nicht möglich'));
                    }
                } catch (err) {
                    reject(err);
                }
            });
        },

        showCopyToast(icon, title) {
            return Swal.fire({
                toast: true,
                position: 'top-end',
                icon,
                title,
                showConfirmButton: false,
                timer: 1800,
                timerProgressBar: true,
            });
        },

        formatLicenseDate(value) {
            if (!value) return 'Unbekannt';
            const parsed = moment(value);
            return parsed.isValid() ? parsed.format('DD.MM.YYYY') : 'Unbekannt';
        },

        buildQuickPaySummary(company, weeks) {
            const normalizedWeeks = Math.max(1, Number(weeks) || 1);
            const weeklyFee = this.getTotalCommercialFees(company || {});
            const currentCycle = company?.nextPaymentLicenses;
            const now = moment();
            const paymentMoment = currentCycle && moment(currentCycle).isValid() ? moment(currentCycle) : null;

            if (!paymentMoment) {
                const nextCycle = moment(now).add(normalizedWeeks, 'weeks');
                return {
                    normalizedWeeks,
                    weeklyFee,
                    overdueWeeks: 0,
                    retroactiveWeeks: 0,
                    arrearsCarryoverWeeks: 0,
                    billedWeeks: normalizedWeeks,
                    weeksUntilNextPayment: normalizedWeeks,
                    totalPrice: weeklyFee * normalizedWeeks,
                    nextCycleIso: nextCycle.toISOString(),
                    nextCycleLabel: nextCycle.format('DD.MM.YYYY'),
                };
            }

            if (paymentMoment.isSameOrAfter(now)) {
                const nextCycle = moment(paymentMoment).add(normalizedWeeks, 'weeks');
                return {
                    normalizedWeeks,
                    weeklyFee,
                    overdueWeeks: 0,
                    retroactiveWeeks: 0,
                    arrearsCarryoverWeeks: 0,
                    billedWeeks: normalizedWeeks,
                    weeksUntilNextPayment: normalizedWeeks,
                    totalPrice: weeklyFee * normalizedWeeks,
                    nextCycleIso: nextCycle.toISOString(),
                    nextCycleLabel: nextCycle.format('DD.MM.YYYY'),
                };
            }

            const overdueWeeks = Math.max(0, now.clone().startOf('day').diff(paymentMoment.clone().startOf('day'), 'weeks'));
            const overdueFullIntervals = Math.floor(overdueWeeks / normalizedWeeks);
            const retroactiveWeeks = overdueFullIntervals * normalizedWeeks;
            const arrearsCarryoverWeeks = overdueWeeks > 0 ? overdueWeeks % normalizedWeeks : 0;
            const billedWeeks = normalizedWeeks + retroactiveWeeks;
            const weeksUntilNextPayment = arrearsCarryoverWeeks > 0 ? normalizedWeeks - arrearsCarryoverWeeks : normalizedWeeks;
            const nextCycle = moment(now).add(weeksUntilNextPayment, 'weeks');

            return {
                normalizedWeeks,
                weeklyFee,
                overdueWeeks,
                retroactiveWeeks,
                arrearsCarryoverWeeks,
                billedWeeks,
                weeksUntilNextPayment,
                totalPrice: weeklyFee * billedWeeks,
                nextCycleIso: nextCycle.toISOString(),
                nextCycleLabel: nextCycle.format('DD.MM.YYYY'),
            };
        },

        calculateQuickPayNextCycle(company, weeks) {
            return this.buildQuickPaySummary(company, weeks).nextCycleIso;
        },

        calculateLicensePaymentDate(weeks, company = null) {
            return this.buildQuickPaySummary(company || this.quickPayCompany, weeks).nextCycleLabel;
        },

        resolvePhoneNumber(person) {
            if (!person || typeof person !== 'object') return 'Unbekannt';

            const candidates = [
                person.phoneNumber,
                person.phone,
                person.telephone,
                person.tel,
                person.phone_number,
            ];

            for (const candidate of candidates) {
                const normalized = `${candidate || ''}`.trim();
                if (normalized.length > 0) {
                    return normalized;
                }
            }

            return 'Unbekannt';
        },

        buildLicenseText(company, paymentSummary = null) {
            const summary = paymentSummary || this.buildQuickPaySummary(company, this.quickPayWeeks);
            const licenseNames = Array.isArray(company?.licenseLinks)
                ? company.licenseLinks
                    .map(link => link?.license?.name)
                    .filter(Boolean)
                : [];
            const signNames = Array.isArray(company?.signLinks)
                ? company.signLinks
                    .map(link => {
                        const iid = link?.sign?.iid;
                        const plz = link?.sign?.postalCode;
                        const label = [`Schild #${iid ?? '?'}`, plz ? `PLZ ${plz}` : null].filter(Boolean).join(' - ');
                        return label;
                    })
                    .filter(Boolean)
                : [];

            const licensesText = licenseNames.length > 0 ? licenseNames.join(', ') : 'Keine';
            const signsText = signNames.length > 0 ? signNames.join(', ') : 'Keine';
            const sharedSignsText = this.getSharedSignLabels(company).join(', ');

            const lines = [
                `Diese Lizenz ist eine Zusammenfassung aus mehreren Lizenzen und Werbeschildern. Das oben genannte Gewerbe ist im Besitzt von folgenden Lizenzen: ${licensesText}. Zugewiesene Werbeschilder: ${signsText}. Diese Lizenz dient der Vereinfachung, um den Arbeitsaufwand zu minimieren und unseren Kunden die Wartezeit zu verkürzen. Wenn gewünscht können die regulären Lizenzen wie gewohnt ausgestellt werden. Genauere Informationen können im §5 GewO Gewerbeordnung eingesehen werden`
            ];

            if (sharedSignsText) {
                lines.push(`Geteilte Schilder an derselben Adresse: ${sharedSignsText}.`);
            }

            lines.push(`Abrechnung für den aktuellen Vorgang: ${summary.normalizedWeeks} Woche:n regulärer Intervall.`);

            if (summary.retroactiveWeeks > 0) {
                lines.push(`Zusätzlich werden ${summary.retroactiveWeeks} Woche:n rückwirkend berechnet, da das Gewerbe seit ${summary.overdueWeeks} Woche:n im Zahlungsverzug ist.`);
            }

            if (summary.arrearsCarryoverWeeks > 0) {
                lines.push(`Es verbleiben ${summary.arrearsCarryoverWeeks} offene Woche:n aus dem Zahlungsverzug, die in den neuen Intervall eingerechnet wurden. Deshalb ist die nächste Zahlung bereits am ${summary.nextCycleLabel} fällig.`);
            } else {
                lines.push(`Die nächste Zahlung ist am ${summary.nextCycleLabel} fällig.`);
            }

            lines.push(`Gesamt abgerechnet werden ${summary.billedWeeks} Woche:n zu ${this.formatMoney(summary.totalPrice)}.`);

            return lines.join('\n');
        },

        buildQuickPayRecordContent(summary) {
            const lines = [
                `Zahlung für **${summary.normalizedWeeks} Woche:n** über **${this.formatMoney(summary.totalPrice)}** erfasst.`
            ];

            if (summary.retroactiveWeeks > 0) {
                lines.push(`Enthält **${summary.retroactiveWeeks} Woche:n** rückwirkende Gebühren wegen Zahlungsverzug.`);
            }

            if (summary.arrearsCarryoverWeeks > 0) {
                lines.push(`**${summary.arrearsCarryoverWeeks} offene Woche:n** wurden in den neuen Intervall übernommen. Die nächste Zahlung ist daher bereits am **${summary.nextCycleLabel}** fällig.`);
            } else {
                lines.push(`Die nächste Zahlung ist am **${summary.nextCycleLabel}** fällig.`);
            }

            return lines.join(' ');
        },

        buildLicenseCopyFields(company, paymentDate, licenseText) {
            const ownerName = company?.owner?.name || 'Unbekannt';
            const ownerPhone = this.resolvePhoneNumber(company?.owner);

            return [
                {
                    label: 'Gewerbe Name',
                    value: company?.name || 'Unbekannt',
                    multiline: false,
                },
                {
                    label: 'Adresse',
                    value: company?.location || 'Unbekannt',
                    multiline: false,
                },
                {
                    label: 'Gewerbe Art',
                    value: this.companyTypes?.[company?.type] || company?.type || 'Unbekannt',
                    multiline: false,
                },
                {
                    label: 'Inhaber',
                    value: ownerName,
                    multiline: false,
                },
                {
                    label: 'Telefonnummer',
                    value: ownerPhone,
                    multiline: false,
                },
                {
                    label: 'Zahlung Termin',
                    value: paymentDate,
                    multiline: false,
                },
                {
                    label: 'Lizenztext',
                    value: licenseText,
                    multiline: true,
                },
            ];
        },

        buildLicenseDialogHtml(fields) {
            return fields.map((field, index) => {
                const escapedLabel = this.escapeHtml(field.label);
                const escapedValue = this.escapeHtml(field.value);

                if (field.multiline) {
                    return `
                        <div style="text-align: left; margin-bottom: 14px;">
                            <div style="font-weight: 700; margin-bottom: 6px;">${escapedLabel}</div>
                            <div style="display: flex; gap: 8px; align-items: flex-start;">
                                <textarea readonly style="flex: 1; min-height: 220px; resize: vertical; padding: 8px; border-radius: 4px; border: 1px solid #c7c7c7; font-size: 13px; font-family: monospace; white-space: pre-wrap; background-color: #ffffff; color: #111111;">${escapedValue}</textarea>
                                <button type="button" data-copy-index="${index}" title="${escapedLabel} kopieren" aria-label="${escapedLabel} kopieren"
                                    style="width: 32px; height: 32px; border: none; border-radius: 4px; cursor: pointer; background-color: #1976d2; color: #fff; display: inline-flex; align-items: center; justify-content: center; flex: 0 0 32px;">
                                    <span class="mdi mdi-content-copy" style="font-size: 16px;"></span>
                                </button>
                            </div>
                        </div>
                    `;
                }

                return `
                    <div style="text-align: left; margin-bottom: 14px;">
                        <div style="font-weight: 700; margin-bottom: 6px;">${escapedLabel}</div>
                        <div style="display: flex; gap: 8px; align-items: center;">
                            <input readonly value="${escapedValue}" style="flex: 1; min-height: 34px; padding: 6px 8px; border-radius: 4px; border: 1px solid #c7c7c7; font-size: 14px; background-color: #ffffff; color: #111111;" />
                            <button type="button" data-copy-index="${index}" title="${escapedLabel} kopieren" aria-label="${escapedLabel} kopieren"
                                style="width: 32px; height: 32px; border: none; border-radius: 4px; cursor: pointer; background-color: #1976d2; color: #fff; display: inline-flex; align-items: center; justify-content: center; flex: 0 0 32px;">
                                <span class="mdi mdi-content-copy" style="font-size: 16px;"></span>
                            </button>
                        </div>
                    </div>
                `;
            }).join('');
        },

        resolveRepresentatives(company) {
            const possibleLists = [
                company?.authorizedRepresentatives,
                company?.authorizedRepresentative,
                company?.representatives,
            ];

            for (const list of possibleLists) {
                if (!Array.isArray(list) || list.length === 0) continue;

                return list.map(entry => {
                    if (entry && typeof entry === 'object') {
                        return {
                            name: entry.name || entry.displayName || '',
                            phoneNumber: entry.phoneNumber || entry.phone || entry.telephone || entry.tel || entry.phone_number || '',
                        };
                    }

                    return {
                        name: `${entry || ''}`.trim(),
                        phoneNumber: '',
                    };
                }).filter(rep => rep.name);
            }

            return [];
        },

        generateLicenseText(company) {
            const companyId = company?.id;

            if (!companyId) {
                Swal.fire({
                    icon: 'error',
                    title: 'Lizenztext konnte nicht generiert werden',
                    text: 'Keine gültige Gewerbe-ID gefunden.',
                });
                return;
            }

            this.apiGetCompanyById(companyId)
                .then(r => {
                    const fullCompany = r?.data || company;
                    const paymentSummary = this.buildQuickPaySummary(fullCompany, this.quickPayWeeks);
                    const paymentDate = paymentSummary.nextCycleLabel;
                    const licenseText = this.buildLicenseText(fullCompany, paymentSummary);
                    const copyFields = this.buildLicenseCopyFields(fullCompany, paymentDate, licenseText);
                    return Swal.fire({
                        title: 'Lizenztext',
                        html: this.buildLicenseDialogHtml(copyFields),
                        width: '800px',
                        confirmButtonText: 'Schließen',
                        didOpen: () => {
                            const copyButtons = document.querySelectorAll('[data-copy-index]');
                            if (!copyButtons.length) return;

                            copyButtons.forEach(copyButton => {
                                copyButton.addEventListener('click', (event) => {
                                    event.preventDefault();
                                    event.stopPropagation();

                                    const index = Number(copyButton.getAttribute('data-copy-index'));
                                    const field = copyFields[index];
                                    if (!field) return;

                                    const icon = copyButton.querySelector('.mdi');
                                    const resetButtonState = () => {
                                        copyButton.style.backgroundColor = '#1976d2';
                                        copyButton.setAttribute('title', `${field.label} kopieren`);
                                        copyButton.setAttribute('aria-label', `${field.label} kopieren`);
                                        if (icon) {
                                            icon.className = 'mdi mdi-content-copy';
                                        }
                                    };

                                    this.copyTextToClipboard(field.value)
                                        .then(() => {
                                            copyButton.style.backgroundColor = '#2e7d32';
                                            copyButton.setAttribute('title', `${field.label} kopiert`);
                                            copyButton.setAttribute('aria-label', `${field.label} kopiert`);
                                            if (icon) {
                                                icon.className = 'mdi mdi-check';
                                            }
                                            window.setTimeout(resetButtonState, 1200);
                                        })
                                        .catch(() => {
                                            copyButton.style.backgroundColor = '#c62828';
                                            copyButton.setAttribute('title', 'Kopieren fehlgeschlagen');
                                            copyButton.setAttribute('aria-label', 'Kopieren fehlgeschlagen');
                                            if (icon) {
                                                icon.className = 'mdi mdi-close';
                                            }
                                            window.setTimeout(resetButtonState, 1200);
                                        });
                                });
                            });
                        },
                    });
                })
                .catch(err => {
                    Swal.fire({
                        icon: 'error',
                        title: 'Lizenztext konnte nicht generiert werden',
                        text: err?.response?.data?.message || err?.message || 'Unbekannter Fehler',
                    });
                });
        },
    },
    mounted() {
        this.fetchCompanies();
    }
}
</script>
